import {
    getTraceBatchOptions,
    searchBatchesByCode,
    type TraceSearchHit,
} from '@api/cm-sites';
import PageHeader from '@components/common/PageHeader';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import { Autocomplete, Button, Group, Stack, Text } from '@mantine/core';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { neutralColors } from '@styles/design-system';
import { useQuery } from '@tanstack/react-query';
import { type FC, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { buildBatchSuggestions } from './batchSuggestions';
import TraceView from './TraceView';

interface TraceContainerProps {
    /** True inside the manufacturer's portal. The server scopes what can be reached either way. */
    asSite?: boolean;
}

/**
 * Finding a batch, then following it.
 *
 * SEARCH FIRST, DELIBERATELY. A trace reachable only by clicking a row you have already found
 * is a trace that is not there when it is needed: in a real recall the code arrives in a
 * supplier's email or off a carton, and the person opening this has a string and nothing else.
 * So the screen opens on a box to type it into, and the row action elsewhere is the shortcut
 * rather than the front door.
 *
 * SEVERAL BATCHES CAN SHARE A CODE. One run packed into three SKUs is three sibling batches;
 * two suppliers can stamp the same string on the same day. So a search shows what it found and
 * lets the person choose, rather than guessing and being confidently wrong about which batch is
 * being recalled.
 *
 * The batch under examination lives in the URL, so a half-finished investigation can be sent to
 * somebody else as a link.
 */
const TraceContainer: FC<TraceContainerProps> = ({ asSite = false }) => {
    const [params, setParams] = useSearchParams();

    const batchId = params.get('batch') ?? '';
    const [code, setCode] = useState(params.get('code') ?? '');
    const [submitted, setSubmitted] = useState(params.get('code') ?? '');

    const { data, isFetching } = useQuery(
        ['trace-search', submitted, asSite],
        () => searchBatchesByCode(submitted, asSite),
        { enabled: submitted.trim().length > 0 },
    );

    const hits = data?.batches ?? [];

    /**
     * Recent batches, offered as you type.
     *
     * SUGGESTIONS, NOT A GATE — the field is an Autocomplete rather than a Select precisely so
     * a code arriving from outside still works. In an everyday lookup nobody remembers how a
     * run was spelled; in a recall the code comes off a carton or out of a supplier's email and
     * may name something this list never had.
     */
    const { data: options } = useQuery(['trace-batch-options', asSite], () =>
        getTraceBatchOptions(asSite),
    );

    const suggestions = useMemo(
        () => buildBatchSuggestions(options?.batches ?? []),
        [options],
    );

    const search = () => {
        setSubmitted(code.trim());
        setParams(code.trim() ? { code: code.trim() } : {});
    };

    const columns: DataSheetColumn<TraceSearchHit>[] = [
        { key: 'code', header: 'Batch', width: 180, sticky: true },
        {
            key: 'itemId',
            header: 'Item',
            width: 240,
            render: (_value: string | null, row) => row.itemName ?? row.itemId ?? '—',
            valueAccessor: (row) => row.itemName ?? row.itemId ?? '',
            csvValue: (_value: string | null, row) => row.itemName ?? row.itemId ?? '',
        },
        {
            key: 'itemType',
            header: 'Kind',
            width: 120,
            render: (value: string | null) => value ?? '—',
        },
        {
            key: '_trace',
            header: '',
            width: 110,
            csvValue: () => '',
            render: (_value, row) => (
                <Button
                    size="xs"
                    variant={ButtonVariant.ACTION}
                    onClick={() => setParams({ code: submitted, batch: row.batchId })}
                >
                    Trace
                </Button>
            ),
        },
    ];

    return (
        <div className="flex h-full w-full flex-col gap-6 page-shell">
            <PageHeader
                icon="🔎"
                title="Trace a batch"
                subtitle="What went into it, what came out of it, and where it went"
            />

            <Group gap={12} align="flex-end">
                <Autocomplete
                    label="Batch code"
                    description="Pick a recent one, or type a code from anywhere"
                    placeholder="LOT-4471"
                    data={suggestions}
                    value={code}
                    onChange={setCode}
                    // Picking a suggestion is the same act as typing it and pressing Find it,
                    // so it searches rather than waiting for a second gesture.
                    onOptionSubmit={(value) => {
                        setCode(value);
                        setSubmitted(value);
                        setParams({ code: value });
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && search()}
                    limit={20}
                    w={320}
                />
                <Button
                    variant={ButtonVariant.ACTION}
                    leftSection={<MagnifyingGlassIcon size={14} />}
                    onClick={search}
                    loading={isFetching}
                    mb={2}
                >
                    Find it
                </Button>
            </Group>

            {submitted && hits.length === 0 && !isFetching && (
                <Text fz={13} c={neutralColors.textSecondary}>
                    Nothing here carries that code.{' '}
                    {asSite
                        ? 'Only batches that passed through this site can be traced from here.'
                        : 'Check the spelling — a batch code is whatever was printed, and is not normalised.'}
                </Text>
            )}

            {hits.length > 0 && !batchId && (
                <Stack gap={8}>
                    {hits.length > 1 && (
                        <Text fz={13} c={neutralColors.textSecondary}>
                            {hits.length} batches carry that code. One run packed into several
                            SKUs looks like this — pick the one being asked about.
                        </Text>
                    )}
                    <DataSheet<TraceSearchHit>
                        data={hits}
                        columns={columns}
                        title="Found"
                        getRowKey={(row) => row.batchId}
                        maxHeight="auto"
                    />
                </Stack>
            )}

            {batchId && (
                <Stack gap={16}>
                    <Group>
                        <Button
                            variant={ButtonVariant.SUBTLE}
                            size="xs"
                            onClick={() => setParams(submitted ? { code: submitted } : {})}
                        >
                            Back to results
                        </Button>
                    </Group>
                    <TraceView
                        batchId={batchId}
                        asSite={asSite}
                        // Following a link keeps the investigation on one screen and in one URL.
                        onTraceBatch={(next) =>
                            setParams({ ...(submitted ? { code: submitted } : {}), batch: next })
                        }
                    />
                </Stack>
            )}
        </div>
    );
};

export default TraceContainer;
